import { Check } from 'lucide-react'
import { Button } from '../ui/shadcn/button'
import { Badge } from '../ui/shadcn/badge'
import {
  enterprisePlan,
  freePlan,
  standardPlan,
  type BillingInterval,
} from '@backend/lib/config/pricing'

type Props = {
  title: string
  price?: number
  annualPrice?: number
  billingInterval?: BillingInterval
  currency: string
  features: string[]
  cta: string
  selected?: boolean
  disabled?: boolean
  onClick: () => void
}

export function PricingCard({
  title,
  price,
  annualPrice,
  billingInterval = 'month',
  currency,
  features,
  cta,
  selected,
  disabled,
  onClick,
}: Props) {
  const isAnnual = billingInterval === 'year'
  const displayPrice = isAnnual && annualPrice ? annualPrice : price
  const monthlyEquivalent = isAnnual && annualPrice ? (annualPrice / 12).toFixed(2) : null
  const buttonGradientStyle =
    title === freePlan.name
      ? 'bg-linear-to-r from-[#ff6b6b] to-[#feca57]'
      : title === enterprisePlan.name
        ? 'bg-linear-to-r from-[#02aab0] to-[#00cdac]'
        : 'bg-linear-to-r from-[#a960ee] to-[#5ffbf1]'

  const checkStyle =
    title === freePlan.name
      ? 'text-[#ff6b6b]'
      : title === enterprisePlan.name
        ? 'text-[#02aab0]'
        : 'text-[#a960ee]'

  const handleClick = () => {
    onClick()
  }
  return (
    <div
      className={`group relative flex h-full flex-col gap-8 overflow-hidden rounded-xl ${title === standardPlan.name ? 'border-2 border-[#a960ee]' : 'border-border border'} bg-card shadow-xs p-6 transition-all hover:-translate-y-1 hover:shadow-md`}>
      <div className="flex h-full flex-1 flex-col justify-between">
        {title === standardPlan.name && !selected && (
          <div className="text- bg-linear-to-r absolute right-4 top-2 rounded-full from-[#a960ee] to-[#5ffbf1] px-4 py-1 text-sm font-medium text-white">
            Most Popular
          </div>
        )}
        {selected && (
          <div className="text-foreground-white bg-muted absolute right-4 top-2 rounded-full px-4 py-1 text-sm font-medium">
            Your Current Plan
          </div>
        )}
        <div className="relative">
          <div className={`'mt-4 mb-2`}>
            <h3 className="text-foreground text-xl font-bold">{title}</h3>
          </div>

          {title !== enterprisePlan.name ? (
            <div className="mb-4">
              <span className="text-foreground text-4xl font-bold">
                {currency === 'eur' ? '€' : '$'}
                {displayPrice}
              </span>
              <span className="text-muted-foreground">
                /{isAnnual && annualPrice ? 'year' : 'month'}
              </span>
              {isAnnual && monthlyEquivalent && (
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">
                    {currency === 'eur' ? '€' : '$'}
                    {monthlyEquivalent}/month
                  </span>
                  <Badge className="bg-linear-to-r border-0 from-[#a960ee] to-[#5ffbf1] text-white">
                    Save 17%
                  </Badge>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <span className="text-foreground text-4xl font-bold">Custom</span>
            </div>
          )}

          <ul className="flex-1 space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className={`mt-1 h-5 w-5 shrink-0 ${checkStyle}`} />
                <span className="text-foreground">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        size="lg"
        className={`mb-4 hover:opacity-90 ${buttonGradientStyle}`}
        onClick={handleClick}
        disabled={disabled}>
        {cta}
      </Button>
    </div>
  )
}
